import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate } from 'react-router-dom';
import { HiShieldCheck } from 'react-icons/hi';
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { updateProfile } from "../features/auth/authSlice";
import axios from "axios";
import { FaUser } from "react-icons/fa";


const Profile = () => {
  const { user, isLoading, isError, message } = useSelector(
    (state) => state.auth,
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");

  // API URL
  // Import API URL from config
  const { API_URL } = require("../config/apiConfig");

  // Load user data and fetch bookings when component mounts
  useEffect(() => {
    if (user) {
      // Set form data from user info
      setFormData({
        name: user.user.name || "",
        email: user.user.email || "",
        phone: user.user.phone || "",
        address: user.user.address || "",
        city: user.user.city || "",
        state: user.user.state || "",
        pincode: user.user.pincode || "",
      });


    }
  }, [user]);

  // Show toast messages on error
  useEffect(() => {
    if (isError) {
      toast.error(message);
    }
  }, [isError, message]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate fields
    if (!formData.name || !formData.email) {
      toast.error("Name and email are required");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    try {
      // Dispatch update profile action
      const result = await dispatch(updateProfile(formData));

      if (result.type === "auth/updateProfile/fulfilled") {
        toast.success("Profile updated successfully!");
        setIsEditing(false);
      } else {
        toast.error("Failed to update profile. Please try again.");
      }
    } catch (error) {
      console.error("Profile update error:", error);
      toast.error("Failed to update profile. Please try again.");
    }
  };



  return (
    <div className="container mx-auto py-8 px-4 mt-16">
      {/* Profile Header */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-col md:flex-row items-center md:items-start justify-between">
          <div className="flex flex-col md:flex-row items-center">
            <div className="w-24 h-24 rounded-full bg-blue-600 text-white flex items-center justify-center text-4xl font-bold mb-4 md:mb-0">
              {formData.name.charAt(0)}
            </div>
            <div className="md:ml-6 text-center md:text-left">
              <h1 className="text-2xl font-bold">{formData.name}</h1>
              <p className="text-gray-600 mb-2">{formData.email}</p>
              <p className="text-gray-600">{formData.phone}</p>
            </div>
          </div>

          <div className="mt-4 md:mt-0 flex items-center space-x-3">
            <button
              onClick={() =>
                setActiveTab("profile") || setIsEditing(!isEditing)
              }
              className={`px-4 py-2 rounded ${isEditing && activeTab === "profile" ? "bg-gray-200 text-gray-800" : "bg-blue-600 text-white"}`}
            >
              {isEditing && activeTab === "profile"
                ? "Cancel Editing"
                : "Edit Profile"}
            </button>

            <button
              onClick={() => {
                const role = (user && user.user && user.user.role) ? user.user.role : 'user';
                // If user is logged in, navigate to change-password (requires current password)
                if (user) {
                  navigate(`/${role}/change-password`);
                } else {
                  // Otherwise, go to forgot-password flow (send OTP)
                  navigate(`/${role}/forgot-password/send-otp`);
                }
              }}
              className="px-4 py-2 rounded bg-amber-500 text-white flex items-center space-x-2 hover:brightness-95"
            >
              <HiShieldCheck className="h-4 w-4" />
              <span>Change Password</span>
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex border-b mb-6 overflow-x-auto">
        <button
          onClick={() => setActiveTab("profile")}
          className={`px-4 py-2 font-medium ${activeTab === "profile" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-500"} flex items-center`}
        >
          <FaUser className="mr-2" /> Profile
        </button>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow-md p-6">
        {/* Profile Tab */}
        {activeTab === "profile" && (
          <>
            {isEditing ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 mb-1">
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full border rounded p-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full border rounded p-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full border rounded p-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="w-full border rounded p-2"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-1">State</label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      className="w-full border rounded p-2"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-1">Pincode</label>
                    <input
                      type="text"
                      name="pincode"
                      value={formData.pincode}
                      onChange={handleChange}
                      className="w-full border rounded p-2"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-gray-700 mb-1">Address</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="w-full border rounded p-2"
                    rows="3"
                    required
                  ></textarea>
                </div>
                <div className="flex justify-end">
                  <button
                    type="submit"
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4">
                    Personal Information
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-gray-600 text-sm">Full Name</p>
                      <p className="font-medium">{formData.name}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 text-sm">Email</p>
                      <p className="font-medium">{formData.email}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 text-sm">Phone</p>
                      <p className="font-medium">
                        {formData.phone || "Not provided"}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4">
                    Address Information
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-gray-600 text-sm">Address</p>
                      <p className="font-medium">
                        {formData.address || "Not provided"}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-gray-600 text-sm">City</p>
                        <p className="font-medium">
                          {formData.city || "Not provided"}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 text-sm">State</p>
                        <p className="font-medium">
                          {formData.state || "Not provided"}
                        </p>
                      </div>
                    </div>
                    <div>
                      <p className="text-gray-600 text-sm">Pincode</p>
                      <p className="font-medium">
                        {formData.pincode || "Not provided"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}


      </div>
    </div>
  );
};

export default Profile;
